import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

interface ArticleSchema {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
}

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  imageUrl: string;
  imageAlt: string;
  imageTitle: string;
  breadcrumbLabel: string;
  articleSchema?: ArticleSchema;
  children: React.ReactNode;
}

export default function ArticleLayout({
  title,
  subtitle,
  imageUrl,
  imageAlt,
  imageTitle,
  breadcrumbLabel,
  articleSchema,
  children,
}: ArticleLayoutProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL;

  const schemaData = articleSchema ? {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: articleSchema.headline,
    description: articleSchema.description,
    image: articleSchema.image,
    datePublished: articleSchema.datePublished,
    dateModified: articleSchema.dateModified,
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet Hub',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': typeof window !== 'undefined' ? window.location.href : siteUrl,
    },
  } : undefined;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      {schemaData && <JsonLd data={schemaData} />}

      {/* Article Hero */}
      <header className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden mt-16">
        <div className="absolute inset-0">
          <img
            src={imageUrl}
            alt={imageAlt}
            title={imageTitle}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
        </div>
        <div className="relative z-10 w-full text-center px-4 md:px-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-5">
            <i className="ri-book-open-line text-background-50 text-sm"></i>
            <span className="text-xs font-medium text-background-50">{subtitle}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-3 max-w-3xl mx-auto leading-tight">
            {title}
          </h1>
          <p className="text-sm md:text-base text-background-50/70 max-w-xl mx-auto">
            Last updated: August 2026
          </p>
        </div>
      </header>

      {/* Breadcrumb Navigation */}
      <nav className="w-full px-4 md:px-6 py-4 border-b border-background-200/70" aria-label="Breadcrumb">
        <div className="max-w-3xl mx-auto flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">Home</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-800 font-medium">{breadcrumbLabel}</span>
        </div>
      </nav>

      {/* Article Content */}
      <article className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <div className="prose-custom space-y-8">
            {children}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}